import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://backoffice.bnywm-cp.com/api/v1';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: (email, password) =>
    apiClient.post('users/sign_in.json', { user: { email, password } }),
  logout: () => apiClient.delete('users/sign_out'),
  register: (email, password, passwordConfirmation) =>
    apiClient.post('users.json', {
      user: { email, password, password_confirmation: passwordConfirmation },
    }),
  getCurrentUser: () => apiClient.get('clients/profile.json'),
};

export const equityService = {
  getAll: () => apiClient.get('equities.json'),
  getById: (id) => apiClient.get(`equities/${id}.json`),
  create: (data) => apiClient.post('equities.json', { equity: data }),
  update: (id, data) => apiClient.patch(`equities/${id}.json`, { equity: data }),
  delete: (id) => apiClient.delete(`equities/${id}.json`),
};

export const bondService = {
  getAll: () => apiClient.get('bonds.json'),
  getById: (id) => apiClient.get(`bonds/${id}.json`),
  create: (data) => apiClient.post('bonds.json', { bond: data }),
  update: (id, data) => apiClient.patch(`bonds/${id}.json`, { bond: data }),
  delete: (id) => apiClient.delete(`bonds/${id}.json`),
};

export const treasuryBondService = {
  getAll: () => apiClient.get('treasury_bonds.json'),
  getById: (id) => apiClient.get(`treasury_bonds/${id}.json`),
  create: (data) => apiClient.post('treasury_bonds.json', { treasury_bond: data }),
  update: (id, data) => apiClient.patch(`treasury_bonds/${id}.json`, { treasury_bond: data }),
  delete: (id) => apiClient.delete(`treasury_bonds/${id}.json`),
};

export const gicService = {
  getAll: () => apiClient.get('gics.json'),
  getById: (id) => apiClient.get(`gics/${id}.json`),
  create: (data) => apiClient.post('gics.json', { gic: data }),
  update: (id, data) => apiClient.patch(`gics/${id}.json`, { gic: data }),
  delete: (id) => apiClient.delete(`gics/${id}.json`),
};

export const orderEquityService = {
  getAll: () => apiClient.get('order_equities.json'),
  getById: (id) => apiClient.get(`order_equities/${id}.json`),
  create: (data) => apiClient.post('order_equities.json', { order_equity: data }),
  buy: () => apiClient.get('order_equities/buy.json'),
  sell: () => apiClient.get('order_equities/sell.json'),
  requestBuy: (equityId, data) =>
    apiClient.post(`order_equities/request_buy.json`, { equity_id: equityId, order_equity: data }),
  requestSell: (equityId, data) =>
    apiClient.post(`order_equities/request_sell.json`, { equity_id: equityId, order_equity: data }),
  approve: (id) => apiClient.patch(`order_equities/${id}/approve.json`),
  reject: (id) => apiClient.patch(`order_equities/${id}/reject.json`),
  delete: (id) => apiClient.delete(`order_equities/${id}.json`),
};

export const orderBondService = {
  getAll: () => apiClient.get('order_bonds.json'),
  getById: (id) => apiClient.get(`order_bonds/${id}.json`),
  create: (data) => apiClient.post('order_bonds.json', { order_bond: data }),
  buy: () => apiClient.get('order_bonds/buy.json'),
  sell: () => apiClient.get('order_bonds/sell.json'),
  requestBuy: (bondId, data) =>
    apiClient.post(`order_bonds/request_buy.json`, { bond_id: bondId, order_bond: data }),
  requestSell: (bondId, data) =>
    apiClient.post(`order_bonds/request_sell.json`, { bond_id: bondId, order_bond: data }),
  approve: (id) => apiClient.patch(`order_bonds/${id}/approve.json`),
  reject: (id) => apiClient.patch(`order_bonds/${id}/reject.json`),
  delete: (id) => apiClient.delete(`order_bonds/${id}.json`),
};

export const orderTreasuryBondService = {
  getAll: () => apiClient.get('order_treasury_bonds.json'),
  getById: (id) => apiClient.get(`order_treasury_bonds/${id}.json`),
  create: (data) =>
    apiClient.post('order_treasury_bonds.json', { order_treasury_bond: data }),
  buy: () => apiClient.get('order_treasury_bonds/buy.json'),
  sell: () => apiClient.get('order_treasury_bonds/sell.json'),
  requestBuy: (tbId, data) =>
    apiClient.post(`order_treasury_bonds/request_buy.json`, { treasury_bond_id: tbId, order_treasury_bond: data }),
  requestSell: (tbId, data) =>
    apiClient.post(`order_treasury_bonds/request_sell.json`, { treasury_bond_id: tbId, order_treasury_bond: data }),
  approve: (id) => apiClient.patch(`order_treasury_bonds/${id}/approve.json`),
  reject: (id) => apiClient.patch(`order_treasury_bonds/${id}/reject.json`),
  delete: (id) => apiClient.delete(`order_treasury_bonds/${id}.json`),
};

export const transactionService = {
  getAll: () => apiClient.get('transactions.json'),
  getById: (id) => apiClient.get(`transactions/${id}.json`),
  getDeposits: () => apiClient.get('transactions/list_deposits.json'),
  getWithdrawals: () => apiClient.get('transactions/list_withdrawals.json'),
  create: (data) =>
    apiClient.post('transactions.json', { transaction: data }),
  requestDeposit: (data) =>
    apiClient.post('transactions/request_deposit_create.json', { transaction: data }),
  requestWithdrawal: (data) =>
    apiClient.post('transactions/request_withdrawal_create.json', { transaction: data }),
  approve: (id) => apiClient.patch(`transactions/${id}/approve.json`),
  reject: (id) => apiClient.patch(`transactions/${id}/reject.json`),
  delete: (id) => apiClient.delete(`transactions/${id}.json`),
};

export const userService = {
  getProfile: () => apiClient.get('clients/profile.json'),
  updateProfile: (data) => apiClient.patch('clients/update_profile.json', { user: data }),
  getBankDetails: () => apiClient.get('clients/bank_details.json'),
  updateBankDetails: (data) =>
    apiClient.patch('clients/update_bank_details.json', { user: data }),
  changePassword: (currentPassword, newPassword) =>
    apiClient.put('clients/change_password.json', {
      user: {
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: newPassword,
      },
    }),
};

export const adminUserService = {
  getAll: () => apiClient.get('clients.json'),
  getById: (id) => apiClient.get(`clients/${id}.json`),
  create: (data) => apiClient.post('clients.json', { user: data }),
  update: (id, data) => apiClient.patch(`clients/${id}.json`, { user: data }),
  delete: (id) => apiClient.delete(`clients/${id}.json`),
  approve: (id) => apiClient.patch(`clients/${id}/approve.json`),
  changePassword: (id, password) =>
    apiClient.patch(`clients/${id}.json`, {
      user: { password, password_confirmation: password },
    }),
  getBankDetailsList: () => apiClient.get('clients/bank_details_list.json'),
  getUserBankDetails: (userId) => apiClient.get('clients/update_bank_details_list.json', { params: { user_id: userId } }),
  updateBankDetailsList: (data) =>
    apiClient.post('clients/request_update_bank_details_list.json', { user: data }),
};

export const dashboardService = {
  getClientDashboard: () => apiClient.get('dashboard/client.json'),
  getAdminDashboard: () => apiClient.get('dashboard/admin.json'),
  getPortfolio: () => apiClient.get('dashboard/portfolio.json'),
};

export default apiClient;
